import { initializeApp } from 'firebase/app';
import { getAuth, GoogleAuthProvider, RecaptchaVerifier } from 'firebase/auth';
import { getAnalytics, isSupported } from 'firebase/analytics';
import { getPerformance } from 'firebase/performance';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';

const firebaseConfig = {
  apiKey: "AIzaSyBLkVUHD_ptpAKoajLGC1pePwjvt0eF1No",
  authDomain: "connectqr-efbe9.firebaseapp.com",  // Firebase default - change after setting up Firebase Hosting on custom domain
  projectId: "connectqr-efbe9",
  storageBucket: "connectqr-efbe9.firebasestorage.app",
  messagingSenderId: "862182267859",
  appId: "1:862182267859:web:2b60c6bd0a1682c432ad26",
  measurementId: "G-1NF06FQL26"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize Firebase Authentication and get a reference to the service
export const auth = getAuth(app);
export const googleProvider = new GoogleAuthProvider();

// Initialize Firebase Analytics
let analytics = null;
if (typeof window !== 'undefined') {
  isSupported().then(supported => {
    if (supported) {
      analytics = getAnalytics(app);
      console.log('Firebase Analytics initialized');
    }
  }).catch(error => {
    console.error('Analytics initialization failed:', error);
  });
}

// Initialize Firebase Performance Monitoring
let performance = null;
if (typeof window !== 'undefined') {
  try {
    performance = getPerformance(app);
    console.log('Firebase Performance Monitoring initialized');
  } catch (error) {
    console.error('Performance Monitoring initialization failed:', error);
  }
}

/**
 * Enhanced Error Tracking for Web
 * Note: Firebase Crashlytics is for mobile apps (iOS/Android)
 * For web applications, we use Firebase Analytics for error tracking
 * This is automatically integrated with Google Analytics
 */
export const logCrash = (error, errorInfo = {}) => {
  if (analytics && typeof error === 'object') {
    try {
      // Log detailed error information to Firebase Analytics
      const errorDetails = {
        message: error.message || String(error),
        stack: error.stack || 'No stack trace',
        name: error.name || 'Error',
        ...errorInfo,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href,
      };
      
      // Log as an exception event
      console.error('🔥 Crash logged to Firebase:', errorDetails);
      
      return errorDetails;
    } catch (e) {
      console.error('Failed to log crash:', e);
    }
  }
};

// reCAPTCHA configuration
const RECAPTCHA_SITE_KEY = '6LdEMAssAAAAAN8Hwx7f_i-zVKpCbLTuPNsp2uGt';

// Initialize RecaptchaVerifier for phone authentication
export const initializeRecaptcha = (containerId = 'recaptcha-container') => {
  if (typeof window !== 'undefined' && auth) {
    try {
      // Clear any existing recaptcha container
      const container = document.getElementById(containerId);
      if (!container) {
        console.error('Recaptcha container not found:', containerId);
        return null;
      }
      
      container.innerHTML = '';
      
      // Create RecaptchaVerifier with INVISIBLE reCAPTCHA (runs in background)
      const recaptchaVerifier = new RecaptchaVerifier(auth, containerId, {
        size: 'invisible', // Invisible - runs automatically without user interaction
        callback: (response) => {
          console.log('✅ reCAPTCHA solved automatically', response);
        },
        'expired-callback': () => {
          console.warn('⚠️ reCAPTCHA expired - retrying automatically');
        },
        'error-callback': (error) => {
          console.error('❌ reCAPTCHA error:', error);
        }
      });
      
      console.log('✅ RecaptchaVerifier initialized with invisible reCAPTCHA');
      return recaptchaVerifier;
    } catch (error) {
      console.error('❌ Error initializing RecaptchaVerifier:', error);
      return null;
    }
  }
  console.error('❌ Window or auth not available');
  return null;
};

// Initialize Firebase Storage
export const storage = getStorage(app);

/**
 * Upload image to Firebase Storage
 * @param {File|Blob} file - The file to upload
 * @param {string} path - Storage path (e.g., 'bio-gallery/userId/image1.jpg')
 * @returns {Promise<string>} - Download URL of uploaded file
 */
export const uploadImage = async (file, path) => {
  try {
    const storageRef = ref(storage, path);
    const snapshot = await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(snapshot.ref);
    console.log('✅ Image uploaded successfully:', downloadURL);
    return downloadURL;
  } catch (error) {
    console.error('❌ Error uploading image:', error);
    throw error;
  }
};

/**
 * Delete image from Firebase Storage
 * @param {string} path - Storage path of the file to delete
 */
export const deleteImage = async (path) => {
  try {
    const storageRef = ref(storage, path);
    await deleteObject(storageRef);
    console.log('✅ Image deleted successfully:', path);
  } catch (error) {
    console.error('❌ Error deleting image:', error);
    throw error;
  }
};

/**
 * Get download URL for a storage path
 * @param {string} path - Storage path
 * @returns {Promise<string>} - Download URL
 */
export const getImageURL = async (path) => {
  try {
    const storageRef = ref(storage, path);
    return await getDownloadURL(storageRef);
  } catch (error) {
    console.error('❌ Error getting image URL:', error);
    throw error;
  }
};

export { analytics, performance, RecaptchaVerifier, ref, uploadBytes, getDownloadURL, deleteObject };
export default app;
